import { uniq } from 'ramda';
import type { Location, NodeEntry } from 'slate';
import { Editor, Node } from 'slate';

import { getNodeSuggestedEdits } from './suggested-edits';
import { isPresent } from '../../util';
import type {
  LocationDirection,
  ParagraphElement,
  RenderModelParagraph,
  SuggestionSourceWithAuthorId,
} from '../internal';
import { isEditWithSource, isParagraphElement, isTextWithSuggestionSource } from '../internal';

export const suggestionWithSourceQueries = {
  /**
   *
   * Check for suggestions with this groupId in the Editor's document.
   */
  docHasSuggestionWithGroupId(editor: Editor, options: { groupId: string }): boolean {
    const { groupId } = options;
    const nodesWithGroupId = Editor.nodes(editor, {
      at: [],
      match: node =>
        getNodeSuggestedEdits(node).some(
          edit => isEditWithSource(edit) && edit.source.groupId === groupId
        ),
      voids: true,
    });
    return Boolean(nodesWithGroupId.next().value);
  },

  /**
   * Find the next paragraph that contains a text node with a suggestion source.
   *
   * We start looking after the last paragraph in the `at` selection.
   */
  findNextParagraphWithEditsWithSource(
    editor: Editor,
    options: { direction: LocationDirection; at?: Location }
  ): NodeEntry<ParagraphElement> | null {
    const { at = editor.selection, direction } = options;
    if (!at) return null;

    const query = direction === 'after' ? Editor.next : Editor.previous;
    const point = direction === 'after' ? Editor.end(editor, at) : Editor.start(editor, at);
    const paragraphEntryAbovePoint = Editor.above(editor, {
      at: point,
      match: isParagraphElement,
    });
    if (!paragraphEntryAbovePoint) return null;

    const [, paraPath] = paragraphEntryAbovePoint;
    const paraEdgePoint =
      direction === 'after' ? Editor.end(editor, paraPath) : Editor.start(editor, paraPath);
    const nextTextEntryWithSource = query(editor, {
      at: paraEdgePoint,
      match: isTextWithSuggestionSource,
    });
    if (!nextTextEntryWithSource) {
      // We've already searched to the end of the document if direction === 'after' and vice/versa if direction === 'before'.  Now search the rest of the document.
      const documentLocation =
        direction === 'after' ? Editor.start(editor, []) : Editor.end(editor, []);
      const textEntry = query(editor, {
        at: documentLocation,
        match: isTextWithSuggestionSource,
      });
      if (!textEntry) return null;

      const [, textPath] = textEntry;
      const paragraphWithSource = Editor.above<ParagraphElement>(editor, {
        at: textPath,
        match: isParagraphElement,
      });

      return paragraphWithSource ?? null;
    }

    const [, textPath] = nextTextEntryWithSource;
    const paragraphWithSource = Editor.above<ParagraphElement>(editor, {
      at: textPath,
      match: isParagraphElement,
    });

    return paragraphWithSource ?? null;
  },

  /**
   * Collect every groupId that still has a sourced suggested edit somewhere in
   * the document, in one traversal. Use this instead of calling
   * `docHasSuggestionWithGroupId` in a loop — that walks the document once per
   * groupId being checked.
   */
  getAllSourcedSuggestionGroupIds(editor: Editor): Set<string> {
    const groupIds = new Set<string>();

    for (const [node] of Editor.nodes(editor, { at: [], voids: true })) {
      for (const edit of getNodeSuggestedEdits(node)) {
        // An empty groupId never names a real group; the type can lie at runtime.
        if (isEditWithSource(edit) && edit.source.groupId) groupIds.add(edit.source.groupId);
      }
    }

    return groupIds;
  },

  /**
   * `getNodeSuggestedEdits` must be imported from `./suggested-edits`
   * directly, not `../internal`: the internal barrel initializes
   * `queries-object` before `queries/suggested-edits`, so this shorthand
   * capture through the barrel would grab `undefined`.
   */
  getNodeSuggestedEdits,

  /**
   * Get the unique suggestion sources from a paragraph.
   */
  getUniqSuggestionSources(
    paragraph: RenderModelParagraph | ParagraphElement
  ): SuggestionSourceWithAuthorId[] {
    const suggestionSources = [...Node.texts(paragraph)].flatMap<SuggestionSourceWithAuthorId>(
      ([text]) => {
        return (
          text.suggestedEdits
            ?.map(suggestedEdit => {
              switch (suggestedEdit.type) {
                case 'insertNode':
                case 'deleteNode':
                  return suggestedEdit.source
                    ? { ...suggestedEdit.source, authorId: suggestedEdit.authorId }
                    : null;
                default:
                  return null;
              }
            })
            .filter(isPresent) ?? []
        );
      }
    );

    return uniq(suggestionSources);
  },
};
